import { useEffect, useMemo, useState } from 'react'
import {
  buildGoogleCalendarLink,
  buildIcalBlobUrl,
} from '../../_utilities/buildCalendarLinks'
import { formatTimezoneCalloutLabel } from '../../_data/timezones'
import {
  formatLongDateInTimezone,
  formatTimeInTimezone,
} from '../../_utilities/formatDateTime'
import { stripHtml } from '../../_utilities/stripHtml'
import type { BookingConfirmationProps } from './types'
import {
  CalendarButtonsRow,
  CalendarLink,
  JoinMeetingLink,
  ConfirmationColumn,
  ManageLinksRow,
  SuccessHeader,
  SuccessHeadline,
  SuccessSubline,
  SummaryCard,
  SummaryLabel,
  SummaryRow,
  SummaryValue,
} from './styled'

const BookingConfirmation = ({
  headline,
  subline,
  bookingId,
  guestName,
  guestEmail,
  startTime,
  endTime,
  timezone,
  hostTimezone,
  eventTypeTitle,
  locationLabel,
  descriptionHtml,
  videoRoomUrl,
  assignedAgentName = '',
  rescheduleToken,
  cancelToken,
}: BookingConfirmationProps): JSX.Element => {
  const plainDescription = useMemo(
    () => stripHtml(descriptionHtml),
    [descriptionHtml],
  )

  const googleCalendarHref = useMemo(
    () =>
      buildGoogleCalendarLink({
        bookingId,
        title: eventTypeTitle,
        description: plainDescription,
        startTime,
        endTime,
        location: locationLabel,
        guestEmail,
      }),
    [
      eventTypeTitle,
      plainDescription,
      startTime,
      endTime,
      locationLabel,
      guestEmail,
    ],
  )

  const [icalHref, setIcalHref] = useState<string>('')

  useEffect(() => {
    const url = buildIcalBlobUrl({
      bookingId,
      title: eventTypeTitle,
      description: plainDescription,
      startTime,
      endTime,
      location: locationLabel,
      guestEmail,
    })
    setIcalHref(url)
    return () => URL.revokeObjectURL(url)
  }, [
    eventTypeTitle,
    plainDescription,
    startTime,
    endTime,
    locationLabel,
    guestEmail,
  ])

  return (
    <ConfirmationColumn>
      <SuccessHeader>
        <SuccessHeadline>{headline}</SuccessHeadline>
        <SuccessSubline>{subline}</SuccessSubline>
      </SuccessHeader>

      <SummaryCard>
        <SummaryRow>
          <SummaryLabel>What</SummaryLabel>
          <SummaryValue>{eventTypeTitle}</SummaryValue>
        </SummaryRow>
        <SummaryRow>
          <SummaryLabel>When</SummaryLabel>
          <SummaryValue>
            {formatLongDateInTimezone(startTime, timezone)}
            <br />
            {formatTimeInTimezone(startTime, timezone)}
            {' – '}
            {formatTimeInTimezone(endTime, timezone)} ({timezone})
            {hostTimezone && hostTimezone !== timezone ? (
              <>
                <br />
                <small>
                  Host&apos;s local:{' '}
                  {formatTimeInTimezone(startTime, hostTimezone)} –{' '}
                  {formatTimeInTimezone(endTime, hostTimezone)} (
                  {formatTimezoneCalloutLabel(hostTimezone)})
                </small>
              </>
            ) : null}
          </SummaryValue>
        </SummaryRow>
        {locationLabel.length > 0 ? (
          <SummaryRow>
            <SummaryLabel>Where</SummaryLabel>
            <SummaryValue>
              {videoRoomUrl.length > 0 ? (
                <JoinMeetingLink
                  href={videoRoomUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Join video call →
                </JoinMeetingLink>
              ) : (
                locationLabel
              )}
            </SummaryValue>
          </SummaryRow>
        ) : null}
        <SummaryRow>
          <SummaryLabel>Who</SummaryLabel>
          <SummaryValue>
            {guestName} ({guestEmail})
          </SummaryValue>
        </SummaryRow>
        {assignedAgentName.length > 0 ? (
          <SummaryRow>
            <SummaryLabel>Host</SummaryLabel>
            <SummaryValue>{assignedAgentName}</SummaryValue>
          </SummaryRow>
        ) : null}
      </SummaryCard>

      <CalendarButtonsRow>
        <CalendarLink
          href={googleCalendarHref}
          target="_blank"
          rel="noopener noreferrer"
        >
          Add to Google Calendar
        </CalendarLink>
        {icalHref.length > 0 ? (
          <CalendarLink
            href={icalHref}
            download="booking.ics"
          >
            Download .ics file
          </CalendarLink>
        ) : null}
      </CalendarButtonsRow>

      {rescheduleToken || cancelToken ? (
        <ManageLinksRow>
          {rescheduleToken ? (
            <span>
              Need to change the time?{' '}
              <a href={`/reschedule/${rescheduleToken}`}>Reschedule</a>
            </span>
          ) : null}
          {cancelToken ? (
            <span>
              Can&apos;t make it?{' '}
              <a href={`/cancel/${cancelToken}`}>Cancel this booking</a>
            </span>
          ) : null}
        </ManageLinksRow>
      ) : null}
    </ConfirmationColumn>
  )
}

export default BookingConfirmation
